// 加工指示の追加ドラフト生成と採番予約をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiDraftService(deps) {
    const {
        DeepCopy,
        DicVal,
        KAKOUSIJI_SAMPLE_GROUP_KEY,
        activeKakousijiKeyRef,
        applyActiveKakousijiSelectionData,
        buildKakousijiEntrySnapshotData,
        buildKakousijiEntrySnapshotDetail,
        buildKakousijiSampleGroupSummaryItem,
        emptyKakousijiSnapshot,
        firstNonEmptyValue,
        fnc,
        isEmpty,
        isKakousijiSampleGroupSummaryItem,
        kakousijiDraftSeqRef,
        kakousijiSampleGroupEntryKeys,
        kakousijiSummaryList,
        lastLoadedKakousijiCodeRef,
        normalizeKakousijiKubunValue,
        normalizeKakousijiSummaryRow,
        resolveCurrentOppiCode,
        setActiveKakousijiKey,
        setKakousijiEntryCache,
        setKakousijiSummaryList,
        syncKakousijiAnchorGridRows
    } = deps;

    function applyKakousijiNoToSnapshot(snapshot, ssdNo) {
        const normalizedSsdNo = String(ssdNo ?? "").trim();
        if (isEmpty(snapshot) || normalizedSsdNo === "") return snapshot;

        const noFieldPattern = /^(SSD|SSDX|SSDN|SSDM|SSDS|SSDSH|SSDF|SSDFH|SSDZM|SSDZ|SSDQ|SSDH|SSDY|SSDB|SSDD|SSDE|SSDGM|SSDSAS|SSDNY)_NO$/i;
        const next = {
            data: DeepCopy(DicVal(snapshot, "data", {})),
            detail: DeepCopy(DicVal(snapshot, "detail", {}))
        };

        Object.keys(next.data).forEach((key) => {
            if (noFieldPattern.test(String(key))) next.data[key] = normalizedSsdNo;
        });
        next.data.SSD_NO = normalizedSsdNo;
        next.data.SSD_SIJINO = normalizedSsdNo;

        Object.keys(next.detail).forEach((type) => {
            next.detail[type] = (Array.isArray(next.detail[type]) ? next.detail[type] : []).map((row) => {
                const copied = isEmpty(row) ? {} : { ...row };
                Object.keys(copied).forEach((key) => {
                    if (noFieldPattern.test(String(key))) copied[key] = normalizedSsdNo;
                });
                return copied;
            });
        });

        return next;
    }
    function appendKakousijiSampleDraft(option, sourceSnapshot, assignedSsdNo, draftNo) {
        const oppiCode = resolveCurrentOppiCode();
        const optionValue = normalizeKakousijiKubunValue(DicVal(option, "code", ""));
        const optionLabel = String(DicVal(option, "name", "")).trim();
        const draftKey = `sample-draft-${Date.now()}-${draftNo}`;
        const baseSnapshot = isEmpty(sourceSnapshot)
            ? emptyKakousijiSnapshot(oppiCode, optionValue)
            : {
                data: buildKakousijiEntrySnapshotData(DicVal(sourceSnapshot, "data", {}), oppiCode, optionValue),
                detail: buildKakousijiEntrySnapshotDetail(DicVal(sourceSnapshot, "detail", {}), oppiCode, optionValue)
            };
        const numberedSnapshot = applyKakousijiNoToSnapshot(baseSnapshot, assignedSsdNo);
        const currentItems = Array.isArray(kakousijiSummaryList) ? kakousijiSummaryList : [];
        const groupIndex = currentItems.findIndex((item) => isKakousijiSampleGroupSummaryItem(item));
        const currentGroup = groupIndex === -1 ? {} : currentItems[groupIndex];
        const nextKeys = [...kakousijiSampleGroupEntryKeys(currentGroup), draftKey];
        const nextGroup = buildKakousijiSampleGroupSummaryItem({
            ...currentGroup,
            draftKubunLabel: optionLabel
        }, nextKeys, draftKey, groupIndex === -1 ? currentItems.length : groupIndex);
        const nextSummaryItems = groupIndex === -1
            ? [...currentItems, nextGroup]
            : currentItems.map((item, index) => index === groupIndex ? nextGroup : item);
        setKakousijiEntryCache(draftKey, numberedSnapshot);
        setKakousijiEntryCache(String(assignedSsdNo ?? '').trim(), numberedSnapshot);
        setKakousijiEntryCache(KAKOUSIJI_SAMPLE_GROUP_KEY, numberedSnapshot);
        setKakousijiSummaryList(nextSummaryItems);
        syncKakousijiAnchorGridRows(nextSummaryItems);
        activeKakousijiKeyRef.current = KAKOUSIJI_SAMPLE_GROUP_KEY;
        lastLoadedKakousijiCodeRef.current = KAKOUSIJI_SAMPLE_GROUP_KEY;
        setActiveKakousijiKey(KAKOUSIJI_SAMPLE_GROUP_KEY);
        applyActiveKakousijiSelectionData(numberedSnapshot.data, numberedSnapshot.detail, {});
    }
    function appendKakousijiDraft(option, sourceSnapshot, assignedSsdNo) {
        const oppiCode = resolveCurrentOppiCode();
        const draftNo = kakousijiDraftSeqRef.current;
        kakousijiDraftSeqRef.current += 1;
        const optionValue = normalizeKakousijiKubunValue(DicVal(option, "code", ""));
        const optionLabel = String(DicVal(option, "name", "")).trim();
        if (optionValue === '0') {
            appendKakousijiSampleDraft(option, sourceSnapshot, assignedSsdNo, draftNo);
            return;
        }
        const draftKey = `draft-${Date.now()}-${draftNo}`;
        const baseSnapshot = isEmpty(sourceSnapshot)
            ? emptyKakousijiSnapshot(oppiCode, optionValue)
            : {
                data: buildKakousijiEntrySnapshotData(DicVal(sourceSnapshot, "data", {}), oppiCode, optionValue),
                detail: buildKakousijiEntrySnapshotDetail(DicVal(sourceSnapshot, "detail", {}), oppiCode, optionValue)
            };
        const numberedSnapshot = applyKakousijiNoToSnapshot(baseSnapshot, assignedSsdNo);
        const draft = normalizeKakousijiSummaryRow({
            key: draftKey,
            oppiCode: oppiCode,
            kubunValue: optionValue,
            code: String(assignedSsdNo ?? "").trim(),
            supplierCode: "",
            supplierName: "",
            updated: "",
            sLoType: String(draftNo),
            isDraft: true,
            draftKubunLabel: optionLabel
        }, kakousijiSummaryList.length);
        setKakousijiEntryCache(draftKey, numberedSnapshot);
        const nextSummaryItems = [...(Array.isArray(kakousijiSummaryList) ? kakousijiSummaryList : []), draft];
        setKakousijiSummaryList(nextSummaryItems);
        syncKakousijiAnchorGridRows(nextSummaryItems);
        setActiveKakousijiKey(draftKey);
    }
    function reserveKakousijiSequence(onSuccess, onError) {
        fnc.current.postaxios("hinbanmaster/GetKakousijiSequence", null,
            {
                onSuccess: function (data) {
                    const ssdNo = String(firstNonEmptyValue(DicVal(data, "returnData", {}), ["ssdNo", "sequence"]) || DicVal(data, "request", "")).trim();
                    if (ssdNo === "") {
                        fnc.current.warnmsg("加工指示採番APIの応答に加工指示番号が含まれていません。");
                        if (typeof onError === "function") onError(data);
                        return;
                    }
                    if (typeof onSuccess === "function") onSuccess(ssdNo);
                },
                onError: function (response) {
                    const message = String(DicVal(response, "errDescription", "")).trim();
                    fnc.current.warnmsg(message === "" ? "加工指示採番APIの呼び出しに失敗しました" : message);
                    if (typeof onError === "function") onError(response);
                }
            }, true
        );
    }

    return {
        applyKakousijiNoToSnapshot,
        appendKakousijiSampleDraft,
        appendKakousijiDraft,
        reserveKakousijiSequence
    };
}
